import { LANGUAGES_MAP, SUPPORTED_LANGUAGES_VALUES } from './constants';

/**
 * Normalizes a raw preferred_lang cookie value to a supported BCP47 locale.
 *
 * Three-step resolution (per MUL cookie conventions):
 *  1. Direct match in SUPPORTED_LANGUAGES_VALUES (handles any full BCP47 code)
 *  2. LANGUAGES_MAP lookup for simple primary codes (de -> de-DE, fr -> fr-FR, etc.)
 *  3. Region-segment upcasing for compound short codes (fr-ca -> fr-CA) -
 *     handles MUL's fr-ca, en-gb style values
 *
 * Returns null if no match is found and the caller decides the fallback value.
 */
export function resolvePreferredLangCookie(raw: string): string | null {
  let val: string;
  try {
    val = decodeURIComponent(raw);
  } catch {
    return null;
  }

  if (SUPPORTED_LANGUAGES_VALUES.includes(val)) {
    return val;
  }

  if (LANGUAGES_MAP[val]) {
    return LANGUAGES_MAP[val];
  }

  const normalized = val.replace(/-([a-z]+)$/, (_, r) => '-' + r.toUpperCase());
  if (SUPPORTED_LANGUAGES_VALUES.includes(normalized)) {
    return normalized;
  }

  return null;
}

/**
 * getBrowserLanguage
 * Returns the browser's preferred language normalized to a supported BCP47 locale.
 * Checks navigator.language directly against supported values first, then applies
 * LANGUAGES_MAP to normalize short codes (e.g. 'de' -> 'de-DE'). Falls back to 'en-US'.
 */
export function getBrowserLanguage(): string {
  const browserLang =
    navigator.language || (navigator.languages?.[0] ?? 'en-US');

  if (SUPPORTED_LANGUAGES_VALUES.includes(browserLang)) {
    return browserLang;
  }

  if (LANGUAGES_MAP[browserLang]) {
    return LANGUAGES_MAP[browserLang];
  }

  return 'en-US';
}

/**
 * getBrowserTimezone
 * This function returns the browser timezone
 * @returns the browser timezone
 */
export function getBrowserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
